import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, CircularProgress, Toolbar, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { messagesService } from '../services/messagesService';
import offerDetailService from '@/features/offer-detail/services/offerDetailService';
import { ConversationDetail, Message } from '../types/messages.types';
import { useSnackbar } from '@/shared/hooks/useSnackbar';
import { useCurrentUser } from '@/features/auth/hooks/useCurrentUser';
import { usePolling } from '@/shared/hooks/usePolling';
import { ConversationDetailHeader } from '../components/ConversationDetailHeader';
import { ConversationOfferBanner } from '../components/ConversationOfferBanner';
import { MessageThread } from '../components/MessageThread';
import { MessageInput } from '../components/MessageInput';

export function ConversationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isFirstScrollRef = useRef(true);
  const { user } = useCurrentUser();

  const [conversation, setConversation] = useState<ConversationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [offerPhotoUrl, setOfferPhotoUrl] = useState<string | null>(null);
  const [offerStatus, setOfferStatus] = useState<string>('PUBLISHED');
  // Refs used by the polling callback to avoid stale closures
  const lastMessageAtRef = useRef<string | null>(null);
  const conversationLoadedRef = useRef(false);
  const isSendingRef = useRef(false);

  const loadConversation = async () => {
    if (!id) {
      setError('Invalid conversation ID');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await messagesService.getConversationMessages(id);
      setConversation(data);
      if (data.messages.length > 0) {
        lastMessageAtRef.current = data.messages[data.messages.length - 1].createdAt;
      }
      conversationLoadedRef.current = true;
      // Mark as read immediately on entry — covers direct-URL and deep-link navigation
      messagesService.markConversationRead(id).catch(() => {});
      // Fetch offer details for photo and status (same data as OfferCard)
      offerDetailService.getOfferById(data.offerId).then((offer) => {
        const firstPhoto = offer.products?.[0]?.photos?.[0];
        setOfferPhotoUrl((firstPhoto?.thumbnailUrl || firstPhoto?.url) ?? null);
        setOfferStatus(offer.status);
      }).catch(() => {});
    } catch (err) {
      console.error('Failed to load conversation:', err);
      setError(t('common.error'));
      showSnackbar(t('messages.loadError'), { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-scroll to bottom whenever messages change; instant on first load to avoid scroll-from-top animation
  useEffect(() => {
    if (conversation?.messages) {
      // Wait for the browser to finish layout before scrolling
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: isFirstScrollRef.current ? 'instant' : 'smooth',
          block: 'end',
        });
        isFirstScrollRef.current = false;
      });
    }
  }, [conversation?.messages]);

  const handleBack = () => {
    navigate('/');
  };

  // Poll for new messages every 3 s while the screen is visible.
  // Uses `since` cursor so only new messages are fetched, not the full thread.
  const pollMessages = useCallback(async () => {
    if (!id || !conversationLoadedRef.current || isSendingRef.current) return;

    const result = await messagesService.getConversationMessages(id, {
      since: lastMessageAtRef.current ?? undefined,
    });

    if (result.messages.length > 0) {
      lastMessageAtRef.current = result.messages[result.messages.length - 1].createdAt;
      setConversation(prev => {
        if (!prev) return null;
        const existingIds = new Set(prev.messages.map(m => m.id));
        const newMessages = result.messages.filter(m => !existingIds.has(m.id));
        if (newMessages.length === 0) return prev;
        return { ...prev, messages: [...prev.messages, ...newMessages] };
      });
      // Auto-marking as read signals the backend to skip the 15s delayed push
      messagesService.markConversationRead(id).catch(() => {});
    }
  }, [id]);

  const { start: startPolling, stop: stopPolling } = usePolling({
    callback: pollMessages,
    interval: 5000,
    pauseOnHidden: true,
    enableBackoff: true,
    maxRetries: 5,
  });

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const handleSendMessage = async (messageBody: string) => {
    if (!conversation || !id || !user?.id) return;

    // Create optimistic message
    const optimisticMessage: Message = {
      id: `temp-${Date.now()}`,
      conversationId: id,
      senderId: user.id,
      body: messageBody,
      readAt: null,
      createdAt: new Date().toISOString(),
    };

    // Add message optimistically to avoid flicker
    setConversation(prev =>
      prev
        ? {
            ...prev,
            messages: [...prev.messages, optimisticMessage],
          }
        : null
    );

    // Scroll immediately
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 50);

    try {
      isSendingRef.current = true;
      setIsSending(true);
      // Reply to existing conversation
      await messagesService.replyToConversation(id, messageBody);

      // Reload full conversation to get real message ID, then sync polling cursor
      const updatedConversation = await messagesService.getConversationMessages(id);
      setConversation(updatedConversation);
      if (updatedConversation.messages.length > 0) {
        lastMessageAtRef.current =
          updatedConversation.messages[updatedConversation.messages.length - 1].createdAt;
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      showSnackbar(t('messages.sendError'), { variant: 'error' });

      // Remove optimistic message on error
      setConversation(prev =>
        prev
          ? {
              ...prev,
              messages: prev.messages.filter(m => m.id !== optimisticMessage.id),
            }
          : null
      );
    } finally {
      setIsSending(false);
      isSendingRef.current = false;
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
        data-testid="conversation-detail-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !conversation) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          px: 3,
        }}
        data-testid="conversation-detail-error"
      >
        <Typography variant="h6" color="error" gutterBottom>
          {t('messages.loadError')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error || t('messages.conversationNotFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      data-testid="conversation-detail-page"
    >
      <ConversationDetailHeader
        name={conversation.interlocutor.name || ''}
        offerTitle={conversation.offerTitle || t('messages.offerTitleFallback')}
        onBack={handleBack}
      />
      <Toolbar />

      <Container maxWidth="md" sx={{ flex: 1, display: 'flex', flexDirection: 'column', px: 0 }}>
        <ConversationOfferBanner
          offerId={conversation.offerId}
          offerTitle={conversation.offerTitle || t('messages.offerTitleFallback')}
          offerPhotoUrl={offerPhotoUrl}
          offerStatus={offerStatus}
        />
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            pt: 2,
            px: 2,
          }}
        >
          <MessageThread messages={conversation.messages} currentUserId="" />
          <div
            ref={messagesEndRef}
            style={{ height: 'calc(120px + env(safe-area-inset-bottom))' }}
          />
        </Box>

        <MessageInput
          onSend={handleSendMessage}
          disabled={isSending}
          placeholder={t('messages.inputPlaceholder')}
        />
      </Container>
    </Box>
  );
}
